import { isPastUnixTime } from '@csp/csp-common-date-util';
import { ApiOptions, Maybe, Patient, StudyOrg, TimeZoneId, VisitVariantType } from '@csp/csp-common-model';
import { findScheduleRequestNextRepeatIndex, RequestActionDateUtil, RequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { axiosDmdp } from '@csp/csp-fe-auth';
import { largePage } from '@csp/dmdp-api-common-dto';
import { uniqWith } from 'lodash';
import { BookedVisit } from '../../model/BookedVisit';
import { VisitService } from '../../service/VisitService';
import { GeneratedVisit } from '../model/GeneratedVisit';
import { VisitGeneratorError } from '../model/VisitGeneratorError';
import { VisitGeneratorErrorCodes } from '../model/VisitGeneratorErrorCodes';
import { VisitRequest } from '../model/VisitRequest';
import { VisitRequestTemplateIn } from '../model/VisitRequestTemplateIn';
import { VisitScheduleService } from './VisitScheduleService';
import { VisitScheduleRolloutService } from './VisitScheduleRolloutService';

const unscheduledVariantComparator = (request1: VisitRequest, request2: VisitRequest): boolean =>
  request1.variant === VisitVariantType.UNSCHEDULED && request2.variant === VisitVariantType.UNSCHEDULED;

const maybeCreateGeneratedVisit = async (visitRequestIn: VisitRequestTemplateIn): Promise<Maybe<GeneratedVisit>> => {
  if (visitRequestIn.startAction?.fixedAction) {
    const nextIndex = findScheduleRequestNextRepeatIndex(
      visitRequestIn.requestCode,
      visitRequestIn.alreadyBookedRequests,
    );
    const endActionTimestamp = RequestActionDateUtil.getEndActionTimestamp(
      visitRequestIn,
      visitRequestIn.scheduleReferenceDates,
    );

    const { requestWindows, isRepeating, isApplicableNow } =
      await VisitScheduleRolloutService.getRolloutRequestResultFromVisitRequestIn({
        visitRequestIn,
        request: visitRequestIn,
        fromRepeatIndex: nextIndex,
        scheduleReferenceDates: visitRequestIn.scheduleReferenceDates,
      });

    const requestWindow = requestWindows[0];
    const endActionNotPassed = !isPastUnixTime(endActionTimestamp?.unixTimeMillis);
    if (requestWindow && isApplicableNow && endActionNotPassed) {
      const requestRef = RequestRef.fromRequestWindow(requestWindow);
      return GeneratedVisit.from(visitRequestIn, isRepeating, requestRef);
    } else {
      return undefined;
    }
  } else {
    /**
     * Visits domain is special in that requests don't have to have a start action.
     * This means that they should always be available and have no window start/end.
     *
     * If these requests would be rolled out with SchedulingService they would never be
     * available because no window intervals can be produced.
     */
    const requestRef = RequestRef.fromGenericRequest(visitRequestIn);
    return visitRequestIn.variant === VisitVariantType.UNSCHEDULED || !visitRequestIn.alreadyBookedRequests.length
      ? GeneratedVisit.from(visitRequestIn, false, requestRef)
      : undefined;
  }
};

const generateScheduledVisits = async (
  patient: Patient,
  studyOrg: StudyOrg,
  timezone: TimeZoneId,
  allBookedVisits?: BookedVisit[],
  apiOptions: ApiOptions = { axios: axiosDmdp() },
): Promise<{ hasScheduledVisits: boolean; unbookedVisits: GeneratedVisit[] }> => {
  const { firstOrgId, userId } = patient;

  if (!firstOrgId) {
    throw new VisitGeneratorError(
      VisitGeneratorErrorCodes.MISSING_PATIENT_INFO,
      `Missing patient information, orgId: ${firstOrgId}`,
    );
  }

  const [{ visits }, activeRequests] = await Promise.all([
    allBookedVisits
      ? Promise.resolve({ visits: allBookedVisits })
      : VisitService.fetchVisits(userId, largePage(), apiOptions),
    VisitScheduleService.getCachedActiveRequestsByOrgId(firstOrgId, apiOptions),
  ]);

  const bookedRequestRefs = visits.map(visit => visit.requestRef);

  // We only allow 1 unscheduled visit. The technical reasons for this is:
  // * both app and web map unscheduled visits to the same content (not by requestRef).
  // * We want to override the default unscheduled visit request in the booking flow.
  const requestsWithMaxOneUnscheduledRequest = uniqWith(activeRequests, unscheduledVariantComparator);

  const unbookedVisits = await Promise.all(
    requestsWithMaxOneUnscheduledRequest
      .map(VisitRequestTemplateIn.from(patient, studyOrg, bookedRequestRefs, timezone))
      .map(async visit => await maybeCreateGeneratedVisit(visit)),
  );

  return {
    hasScheduledVisits: !!activeRequests.length,
    unbookedVisits: unbookedVisits.filter(isDefined),
  };
};

export const VisitsGeneratorService = {
  generateScheduledVisits,
  maybeCreateGeneratedVisit,
};
